import axios, { AxiosRequestConfig, AxiosResponse, AxiosInstance } from 'axios';
import { ACCESS_TOKEN, APP_REFERER, REFRESH_TOKEN } from '../../constants';
import { cookiesHelper } from '../../helpers';

let tokenRefreshPromise: Promise<string | undefined> | null = null;

/**
 * Internal function to refresh the access token.
 * Ensures only one refresh request is made at a time.
 */
const refreshTokenInternal = async (
  appName: APP_REFERER,
): Promise<string | undefined> => {
  // If a refresh request is already in progress, return the existing promise
  if (tokenRefreshPromise) return tokenRefreshPromise;

  tokenRefreshPromise = (async () => {
    const accessTokenExpired = cookiesHelper.get(ACCESS_TOKEN);
    const refreshToken = cookiesHelper.get(REFRESH_TOKEN);

    if (!refreshToken) {
      redirectToLogin(appName);
      return undefined;
    }

    const axiosInstance = axios.create({
      baseURL: process.env['NX_AUTH_API_URL'],
    });

    try {
      const { data } = await axiosInstance.post('/auth/refresh-token', {
        accessTokenExpired,
        refreshToken,
      });

      const newAccessToken = data.accessToken.jwtToken;
      cookiesHelper.set(ACCESS_TOKEN, newAccessToken);
      return newAccessToken;
    } catch (error) {
      clearAuthCookies();
      redirectToLogin(appName);
      return undefined;
    }
  })();

  try {
    return await tokenRefreshPromise;
  } finally {
    tokenRefreshPromise = null;
  }
};

export const renewAccessToken = async (
  appName: APP_REFERER = APP_REFERER.PAYROLLR,
  originalAxiosInstance?: AxiosInstance,
  config?: AxiosRequestConfig,
): Promise<void | AxiosResponse<any> | string> => {
  const newAccessToken = await refreshTokenInternal(appName);

  if (!newAccessToken) {
    return;
  }

  // If an originalAxiosInstance and config are provided, retry the request with the new token
  if (originalAxiosInstance && config) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${newAccessToken}`,
    };
    return originalAxiosInstance(config);
  }

  return newAccessToken;
};

const clearAuthCookies = () => {
  const domainOptions = { path: '/', domain: process.env['NX_DOMAIN_NAME'] };
  cookiesHelper.remove(ACCESS_TOKEN, domainOptions);
  cookiesHelper.remove(REFRESH_TOKEN, domainOptions);
};

const redirectToLogin = (appName: APP_REFERER) => {
  window.location.href = `${process.env['NX_AUTH_PUBLIC_URL']}/login?appReferer=${appName}`;
};
